/// React bindings for the soundtrack engine.
///
/// `useSoundtrackEngine()` returns the singleton (always the same ref,
/// no React state churn). `useSoundtrackState()` re-renders on engine
/// state changes (settings + active cue url).
///
/// The engine is URL-based — there is no client-side cue resolution.
/// All cue selection happens on the server, which sends `soundtrack_play`
/// / `fx_play` WS messages to the client. The audio-routing module
/// dispatches those into engine.playSoundtrack/playFx.

import { useSyncExternalStore } from 'react';
import type { SoundtrackEngine } from './soundtrack-engine';
import { getSoundtrackEngine } from './soundtrack-engine';

export function useSoundtrackEngine(): SoundtrackEngine {
  return getSoundtrackEngine();
}

export interface SoundtrackState {
  /// True iff the channel is producing sound (audio on AND volume > 0).
  enabled: boolean;
  /// Global audio on/off switch — independent of volume.
  audioOn: boolean;
  volume: number;
  currentSoundtrackUrl: string | null;
}

// Cache the snapshot so `useSyncExternalStore` sees a stable reference
// between renders. Without this, the arrow body creates a fresh object
// every call → React's `Object.is` identity check fails → infinite
// re-render loop (caught by ErrorBoundary, which shows NotFoundPage).
// Only allocate a new snapshot when one of the engine's reactive
// values actually changed.
let _cachedSnapshot: SoundtrackState = {
  enabled: true,
  audioOn: true,
  volume: 1,
  currentSoundtrackUrl: null,
};

function readSnapshot(engine: SoundtrackEngine): SoundtrackState {
  const enabled = engine.isEnabled();
  const audioOn = engine.isAudioOn();
  const volume = engine.getVolume();
  const currentSoundtrackUrl = engine.getCurrentSoundtrackUrl();
  if (
    _cachedSnapshot.enabled === enabled &&
    _cachedSnapshot.audioOn === audioOn &&
    _cachedSnapshot.volume === volume &&
    _cachedSnapshot.currentSoundtrackUrl === currentSoundtrackUrl
  ) {
    return _cachedSnapshot;
  }
  _cachedSnapshot = { enabled, audioOn, volume, currentSoundtrackUrl };
  return _cachedSnapshot;
}

const _serverSnapshot: SoundtrackState = {
  enabled: true,
  audioOn: true,
  volume: 1,
  currentSoundtrackUrl: null,
};

export function useSoundtrackState(): SoundtrackState {
  const engine = getSoundtrackEngine();
  return useSyncExternalStore(
    (cb) => engine.subscribe(cb),
    () => readSnapshot(engine),
    () => _serverSnapshot
  );
}
